import type { Swiper } from "swiper11";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { listToGroup } from "./utils";
import type { AnimateName, YIGEAnimationOptions, YIGEAnimationConstructor, AnimationObject, GroupItem } from "@common/types/commonTypes/YIGEAnimation";
export const presetGSAP_IN = <T extends gsap.TweenVars>(opts?: T) => {
  const themeColorFadeIn = {
    color: () => {
      const themeColor = $("body").css("--themeColor");
      return themeColor;
    },
    duration: 1.65,
    ease: "sine.out",
    ...opts,
  } as T;
  const bgThemeFadeIn = {
    backgroundColor: () => {
      const themeColor = $("body").css("--themeColor");
      return themeColor;
    },
    duration: 1.4,
    ...opts,
  } as T;
  const bgColorFadeIn = {
    backgroundColor: "transparent",
    duration: 1.65,
    ...opts,
  } as T;
  const fadeInUp = {
    autoAlpha: 0,
    y: 60,
    ease: "power4.out",
    duration: 1,
    ...opts,
  } as T;
  const fadeInBottom = {
    autoAlpha: 0,
    y: -60,
    ease: "power4.out",
    duration: 1,
    ...opts,
  } as T;
  const fadeInLeft = {
    autoAlpha: 0,
    x: -60,
    ease: "power4.out",
    duration: 1,
    ...opts,
  } as T;
  const fadeInRight = {
    autoAlpha: 0,
    x: 60,
    ease: "power4.out",
    duration: 1,
    ...opts,
  } as T;
  const fadeIn = {
    autoAlpha: 0,
    ease: "power3.out",
    duration: 1,
    ...opts,
  } as T;
  const clipTop = {
    clipPath: "inset(0% 0% 100% 0%)",
    ease: "power4.out",
    duration: 1.4,
    ...opts,
  } as T;
  const clipBottom = {
    clipPath: "inset(100% 0% 0% 0%)",
    ease: "power4.out",
    duration: 1.4,
    ...opts,
  } as T;
  const clipLeft = {
    clipPath: "inset(0% 100% 0% 0%)",
    ease: "power4.out",
    duration: 1.4,
    ...opts,
  } as T;
  const clipRight = {
    clipPath: "inset(0% 0% 0% 100%)",
    ease: "power4.out",
    duration: 1.4,
    ...opts,
  } as T;
  const clipInUp = {
    clipPath: "inset(0% 0% 100% 0%)",
    yPercent: 100,
    ease: "power4.out",
    duration: 1,
    ...opts,
  } as T;
  const clipInBottom = {
    clipPath: "inset(100% 0% 0% 0%)",
    yPercent: -100,
    ease: "power4.out",
    duration: 1,
    ...opts,
  } as T;
  const clipInLeft = {
    clipPath: "inset(0% 0% 0% 100%)",
    xPercent: -100,
    ease: "power4.out",
    duration: 1,
    ...opts,
  } as T;
  const clipInRight = {
    clipPath: "inset(0% 100% 0% 0%)",
    xPercent: 100,
    ease: "power4.out",
    duration: 1,
    ...opts,
  } as T;
  const clipInCenter = {
    clipPath: "inset(20%)",
    duration: 1,
    ease: "expo.out",
    ...opts,
  } as T;
  const scaleInCenter = {
    scale: 1.5,
    autoAlpha: 0,
    duration: 1.4,
    ease: "expo.out",
    ...opts,
  } as T;
  return {
    themeColorFadeIn,
    bgThemeFadeIn,
    bgColorFadeIn,
    fadeIn,
    fadeInUp,
    fadeInBottom,
    fadeInLeft,
    fadeInRight,
    clipTop,
    clipBottom,
    clipRight,
    clipLeft,
    clipInUp,
    clipInBottom,
    clipInLeft,
    clipInRight,
    clipInCenter,
    scaleInCenter,
  };
};
export const presetGSAP_OUT = <T extends gsap.TweenVars>(opts?: T) => {
  const fadeOutUp = {
    autoAlpha: 0,
    y: 60,
    ease: "power4.out",
    duration: 1,
    ...opts,
  } as T;

  return {
    fadeOutUp,
  };
};

export const swiperGsapTimeline = (swiper: Swiper, opts?: SwiperGsapTimeline.Options): gsap.core.Timeline | undefined => {
  if (!swiper || !swiper.el) {
    console.warn("Swiper instance or element is not valid");
    return;
  }
  gsap.registerPlugin(ScrollTrigger);
  const isAutoPlay = swiper.autoplay && swiper.autoplay.running;
  if (isAutoPlay) {
    swiper.autoplay.stop();
  }
  const T = gsap.timeline({
    paused: true,
    onComplete: function () {
      if (isAutoPlay) {
        swiper.autoplay.start();
      }
      if (opts?.onComplate) {
        opts.onComplate();
      }
      this.kill();
    },
  });

  const id = new Date().getTime().toString();

  // 确保 Swiper 元素已完全渲染
  requestAnimationFrame(() => {
    try {
      // 确保 swiper.el 已经是有效的 DOM 元素
      if (swiper.el && swiper.el instanceof HTMLElement) {
        ScrollTrigger.create({
          id,
          trigger: swiper.el,
          start: opts?.start || "top bottom-=20%",
          end: opts?.start || "top bottom-=20%",
          onEnter: () => {
            T.play();
          },
        });
      } else {
        console.warn("Swiper element is not a valid DOM element.");
      }
    } catch (error) {
      console.warn("Error with ScrollTrigger:", error);
      // 确保报错时继续执行剩余逻辑
      T.play();
    }
    ScrollTrigger.refresh();
  });

  return T;
};

// export namespace YIGEAnimation {
//   export type AnimateName = keyof ReturnType<typeof presetGSAP_IN> | keyof ReturnType<typeof presetGSAP_OUT>;
//   export interface Options {
//     type?: "in" | "out";
//     selector?: HTMLElement;
//     start?: string | number | ScrollTrigger.StartEndFunc | undefined;
//     onComplate?: () => void;
//   }
//   export interface Constructor extends Options {
//     singleArray?: AnimationObject[];
//     GroupArray: any;
//     LineArray: any;
//   }

//   export type AnimationObject = {
//     name: string;
//     element: HTMLElement | HTMLElement[];
//     animate: gsap.TweenVars;
//     id?: string | number;
//     g?: gsap.core.Tween | null;
//     complate: boolean;
//   };

//   export interface GroupItem {
//     marker: HTMLElement[];
//     groupName: string;
//     list: JQuery<HTMLElement>[] | null;
//     children: AnimationObject[];
//   }
// }
export class YIGEAnimation implements YIGEAnimationConstructor {
  // 用静态属性保存首次创建的实例
  private static instance: YIGEAnimation;

  // 用来保存实例化时的选项
  private static initialized = false;

  type;
  selector;
  start;
  singleArray;
  GroupArray;
  LineArray;
  once;
  onComplate: YIGEAnimationOptions["onComplate"] = undefined;
  complate: boolean = false;

  constructor(options?: YIGEAnimationOptions) {
    // 如果已经初始化过实例，则直接返回现有实例
    if (YIGEAnimation.instance && YIGEAnimation.initialized) {
      return YIGEAnimation.instance;
    }

    // 否则，初始化实例并保存
    if (!YIGEAnimation.instance) {
      YIGEAnimation.instance = this;
      YIGEAnimation.initialized = true;
    }

    const { selector = document.querySelector("body")!, type = "in", once = true, start = "top bottom-=15%" } = options || {};
    if (!selector) {
      throw new Error("YIGEAnimation 无有效的选择器元素！");
    }
    this.type = type;
    this.selector = selector;
    this.start = start;
    this.once = once;
    const { single, group, line } = this.getElements(selector);
    this.singleArray = this.#processSingleList(single);
    this.GroupArray = this.#processCollection(group, "group");
    this.LineArray = this.#processCollection(line, "timeline");

    this.#initSingleEl(this.singleArray);
    this.#initCollectionEl(this.GroupArray);
    this.#initCollectionEl(this.LineArray);
    this.#registerEvent();
  }

  // 以下为类的其他方法...
  #processCollection(group: { [key: string]: HTMLElement[] }, type: "group" | "timeline") {
    const returnList: GroupItem[] = [];
    Object.keys(group).forEach((key) => {
      const groupItems = group[key];
      if (!groupItems.length) return;

      const returnItem: GroupItem = {
        marker: groupItems,
        groupName: key,
        list: listToGroup($(groupItems)),
        children: [],
      };
      switch (type) {
        case "group":
          break;
        case "timeline":
          returnItem.list = null;
          break;
        default:
          break;
      }
      const children = $(groupItems).find("[yg-ani]");
      children.each((_i, item) => {
        const str = $(item).attr("yg-ani") || "";
        const match = this.#getGroupIndex(str);
        if (!match) return;
        const [_, idx, aniStr] = match;
        const presetAnimate = this.getPresetAnimate(aniStr);
        // 优化：提前构建索引来避免遍历整个 children 数组
        const existingItem = returnItem.children.find((child) => child.id === idx);
        if (existingItem) {
          existingItem.element = Array.isArray(existingItem.element) ? [...existingItem.element, item] : [existingItem.element, item];
          existingItem.name = Array.from(new Set(existingItem.name.split(",").concat(aniStr))).join(",");
          existingItem.animate = this.getPresetAnimate(existingItem.name);
        } else {
          returnItem.children.push({
            id: idx,
            name: aniStr,
            element: [item],
            animate: presetAnimate,
            complate: false,
          });
        }
      });
      returnList.push(returnItem);
    });
    return returnList;
  }

  getElements(selector: HTMLElement) {
    const animateElements: HTMLElement[] = Array.from(selector.querySelectorAll("[yg-ani]"));
    const groupElements: HTMLElement[] = Array.from(selector.querySelectorAll("[yg-ani-group]"));
    const lineElements: HTMLElement[] = Array.from(selector.querySelectorAll("[yg-ani-line]"));

    const initializedList: { single: HTMLElement[]; group: { [key: string]: HTMLElement[] }; line: { [key: string]: HTMLElement[] } } = {
      single: [],
      group: {},
      line: {},
    };
    animateElements.forEach((el) => {
      const name = el.getAttribute("yg-ani") as AnimateName;
      const group = this.#isCollection(el, "yg-ani-group");
      const line = this.#isCollection(el, "yg-ani-line");
      if (!name) return;
      if (!group && !line) {
        initializedList.single.push(el);
      }
    });
    groupElements.forEach((el) => {
      const name = el.getAttribute("yg-ani-group") as AnimateName;
      if (!initializedList.group[name]) {
        initializedList.group[name] = [];
      }
      initializedList.group[name].push(el);
    });

    lineElements.forEach((el) => {
      const name = el.getAttribute("yg-ani-line") as AnimateName;
      if (!initializedList.line[name]) {
        initializedList.line[name] = [];
      }
      initializedList.line[name].push(el);
    });
    return initializedList;
  }

  #processSingleList(list: HTMLElement[]) {
    const elArray: AnimationObject[] = [];
    list.forEach((el) => {
      const name = el.getAttribute("yg-ani") as AnimateName;
      let presetAnimate: gsap.TweenVars = {};
      if (!name) return;
      presetAnimate = this.getPresetAnimate(name) || {};
      const duration = this.getValue("yg-ani-duration", el, presetAnimate.duration || 0.85);
      const delay = this.getValue("yg-ani-delay", el, presetAnimate.delay || 0);
      const ease = el.getAttribute("yg-ani-ease") || presetAnimate.ease || "power4.out";
      presetAnimate = { ...presetAnimate, duration, delay, ease };
      const presetObject: AnimationObject = {
        element: el,
        name,
        animate: presetAnimate,
        g: undefined,
        complate: false,
      };
      elArray.push(presetObject);
    });
    return elArray;
  }

  isNumber(attr: string | number | null): boolean {
    return !(isNaN(Number(attr)) || !attr);
  }

  getValue(key: string, el: Element, defaultVal: gsap.TweenValue) {
    return this.isNumber(el.getAttribute(key)) ? Number(el.getAttribute(key)) : defaultVal;
  }

  #initSingleEl(arr: AnimationObject[]) {
    if (!arr || arr.length === 0) return;
    gsap.registerPlugin(ScrollTrigger);
    arr.forEach((item) => {
      let g: gsap.core.Tween | undefined | null = undefined;
      const trigger = ScrollTrigger.create({
        trigger: item.element,
        start: this.start,
        once: this.once,
      });
      const trans = this.resetTransition(item.element as HTMLElement);
      const params: gsap.TweenVars = {
        scrollTrigger: trigger,
        ...item.animate,
        onComplete: function () {
          if (trans) {
            gsap.set(item.element, { transition: trans });
          }

          if (this.scrollTrigger) {
            (this.scrollTrigger as ScrollTrigger).kill();
          }

          this.kill();
          g = null;
          item.g = null;
          item.complate = true;
        },
      };
      if (this.type === "in") {
        g = gsap.from(item.element, params);
      } else if (this.type === "out") {
        g = gsap.to(item.element, params);
      }
      item.g = g;
    });
  }

  #initCollectionEl(arr: GroupItem[]) {
    if (!arr || arr.length === 0) return;
    gsap.registerPlugin(ScrollTrigger);
    arr.forEach((groupItem) => {
      if (groupItem.list) {
        groupItem.list.forEach((row) => {
          const T = gsap.timeline({
            defaults: {
              stagger: 0.1,
            },
            id: groupItem.groupName,
            scrollTrigger: {
              trigger: row[0],
              id: groupItem.groupName,
              start: this.start,
              once: this.once,
            },
          });
          groupItem.children.forEach((aniChild) => {
            const els = $(row).find($(aniChild.element));
            T.from(els, aniChild.animate, "<5%");
          });
        });
      } else if (groupItem.children) {
        const T = gsap.timeline({
          id: groupItem.groupName,
          scrollTrigger: {
            trigger: groupItem.marker,
            id: groupItem.groupName,
            start: this.start,
            once: this.once,
          },
        });
        groupItem.children.forEach((aniChild) => {
          T.add(gsap.from(aniChild.element, { ...aniChild.animate, stagger: 0.1 }), "<10%");
        });
      }
    });
  }

  #registerEvent() {}
  #isCollection(el: Element, key: string) {
    const groupParent = $(el).parents(`[${key}]`);
    if (groupParent.length) {
      return true;
    }
    return false;
  }

  #getGroupIndex(str: string) {
    const regex = /^\[([^\]]+)\](.*)$/;
    const match = str.match(regex);
    if (match) {
      return match;
    }
    return false;
  }

  resetTransition(el: HTMLElement) {
    const getter = gsap.getProperty(el);
    const duration = getter("transition-duration");
    const transition = duration ? getter("transition-property") + " " + (duration + "s") + " " + (getter("transition-delay") + "s") : false;
    if (transition) {
      gsap.set(el, { transition: "none" });
    }
    return transition;
  }

  public getPresetAnimate(nameStr: AnimateName | string) {
    let presetAnimate: gsap.TweenVars = {};
    const nameArray: AnimateName[] = [];
    if (nameStr.includes(",")) {
      nameArray.push(...(nameStr.split(",") as AnimateName[]));
    } else {
      nameArray.push(nameStr as AnimateName);
    }
    nameArray.forEach((name) => {
      if (this.type === "in") {
        const presetIn = presetGSAP_IN();
        if (name in presetIn) {
          presetAnimate = { ...presetAnimate, ...presetIn[name as keyof typeof presetIn] };
        } else {
          console.warn(`presetGSAP_IN 中没有 "${name}" 属性`);
          return;
        }
      } else {
        const presetOut = presetGSAP_OUT();
        if (name in presetOut) {
          presetAnimate = { ...presetAnimate, ...presetOut[name as keyof typeof presetOut] };
        } else {
          console.warn(`presetGSAP_OUT 中没有 "${name}" 属性`);
          return;
        }
      }
    });
    return presetAnimate;
  }

  public update(target?: AnimationObject["g"]) {
    if (target && target.scrollTrigger) {
      target.scrollTrigger.refresh();
      return;
    } else {
      if (this.singleArray) {
        this.singleArray.forEach((item) => {
          if (item.g && item.g.scrollTrigger) {
            item.g.scrollTrigger.refresh();
          }
        });
      }
      return;
    }
  }
}
